// import external libraries
import { useTranslation } from 'react-i18next';

// import components
import { MultiSelect } from 'components/shared';

// import hooks
import useGetEmployeesBasedOnCostCenters from 'components/roster-portal/RosterPlan/hooks/useGetEmployeesBasedOnCostCenters';

// import utils
import { formatEntityDisplayLabel } from 'components/mrp/mrp.utils';
import { costCentersOf, renderFilterTags } from '../../BacklogFilters.utils';

// import types
import type { FilterOption } from '../../../../Backlog.types';
import type { BacklogPlanUnitDrivenFilterProps } from './BacklogEmployeeFilter.types';

/**
 * The employees of the picked plan units, as a ready list rather than a tenant-wide search.
 *
 * Uses the cost-centre employees endpoint the roster plan already calls, so no new call is introduced and a
 * planner picks from the people the plan unit actually holds. Empty until a plan unit is picked: without one
 * there is no list to offer, and asking for every employee in the tenant is the request this whole page is
 * trying to avoid.
 */
const BacklogEmployeeFilter = ({
  planUnits,
  startDate,
  endDate,
  selected,
  onChange
}: BacklogPlanUnitDrivenFilterProps) => {
  'use memo';

  const { t } = useTranslation('common');
  const costCenters = costCentersOf(planUnits);

  const { data = [] } = useGetEmployeesBasedOnCostCenters(
    { costCenters, startDate, endDate },
    { enabled: !!costCenters }
  );

  const options: FilterOption[] = data.map(({ id, nameCompound, number }) => ({
    id: String(id),
    // Named the way afsprakenplanning names a person, so the same employee reads the same everywhere.
    name: formatEntityDisplayLabel(nameCompound, number)
  }));

  return (
    <MultiSelect<FilterOption>
      label={t('mrp.entityTypeMap.EMPLOYEE.single')}
      options={options}
      selectedOptions={selected}
      onChange={onChange}
      optionLabelProp="name"
      // Emptied in one go rather than unticked value by value — see BacklogActivityFilter.
      disableClearable={false}
      disabled={!costCenters}
      renderTags={renderFilterTags(
        ({ name }) => name,
        t('selected').toLowerCase()
      )}
    />
  );
};

export default BacklogEmployeeFilter;
